import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api, API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import {
  Loader2, Search, Filter, Download, FileDown, CheckCircle2, XCircle, Ban, Mail, RefreshCw,
  AlertTriangle, TrendingUp, Wallet, Receipt, Clock, MoreHorizontal, FileText, History,
} from 'lucide-react';
import { toast } from 'sonner';

const ESTADOS = [
  { v: 'todos', label: 'Todos los estados' },
  { v: 'pendiente', label: 'Pendiente' },
  { v: 'pagada', label: 'Pagada' },
  { v: 'impago', label: 'Impago' },
  { v: 'fallida', label: 'Fallida' },
  { v: 'anulada', label: 'Anulada' },
  { v: 'rectificativa', label: 'Rectificativa' },
];

const METODOS = [
  { v: 'todos', label: 'Todos los métodos' },
  { v: 'tarjeta', label: 'Tarjeta' },
  { v: 'transferencia', label: 'Transferencia' },
  { v: 'sepa', label: 'SEPA (domiciliación)' },
];

const CICLOS = [
  { v: 'todos', label: 'Todos los ciclos' },
  { v: 'mensual', label: 'Mensual' },
  { v: 'anual', label: 'Anual' },
  { v: 'anual_ampliacion', label: 'Ampliación anual' },
];

const estadoBadge = (estado) => {
  const map = {
    pendiente: 'bg-amber-50 text-amber-800 border-amber-200',
    pagada: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    impago: 'bg-orange-50 text-orange-800 border-orange-200',
    fallida: 'bg-red-50 text-red-800 border-red-200',
    anulada: 'bg-zinc-100 text-zinc-600 border-zinc-300 line-through',
    rectificativa: 'bg-purple-50 text-purple-800 border-purple-200',
  };
  return map[estado] || 'bg-zinc-50 text-zinc-700 border-zinc-200';
};

const metodoLabel = (m) => ({ tarjeta: 'Tarjeta', transferencia: 'Transferencia', sepa: 'SEPA' }[m] || m || '—');

const fmtEUR = (n) => `${(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function FacturasGlobalTab() {
  const [kpis, setKpis] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    q: '', empresa_id: 'todos', estado: 'todos', metodo: 'todos', ciclo: 'todos',
    fecha_desde: '', fecha_hasta: '', numero: '', importe_min: '', importe_max: '',
  });
  const [modal, setModal] = useState(null); // { tipo, factura }
  const [timeline, setTimeline] = useState(null);

  const cargarKpis = useCallback(() => api.get('/superadmin/facturas/kpis').then(r => setKpis(r.data)).catch(() => {}), []);
  const cargarEmpresas = useCallback(() => api.get('/superadmin/empresas').then(r => setEmpresas(r.data)).catch(() => {}), []);

  const cargarFacturas = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filtros.empresa_id && filtros.empresa_id !== 'todos') params.empresa_id = filtros.empresa_id;
    if (filtros.estado && filtros.estado !== 'todos') params.estado = filtros.estado;
    if (filtros.metodo && filtros.metodo !== 'todos') params.metodo = filtros.metodo;
    if (filtros.ciclo && filtros.ciclo !== 'todos') params.ciclo = filtros.ciclo;
    if (filtros.numero) params.numero = filtros.numero;
    if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
    if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
    if (filtros.importe_min) params.importe_min = parseFloat(filtros.importe_min);
    if (filtros.importe_max) params.importe_max = parseFloat(filtros.importe_max);
    try {
      const r = await api.get('/superadmin/facturas', { params });
      setFacturas(r.data || []);
    } catch (e) {
      toast.error('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargarKpis(); cargarEmpresas(); }, [cargarKpis, cargarEmpresas]);
  useEffect(() => { cargarFacturas(); }, [cargarFacturas]);

  // Búsqueda libre (empresa nombre, número, importe)
  const visibles = useMemo(() => {
    const q = (filtros.q || '').toLowerCase().trim();
    if (!q) return facturas;
    return facturas.filter(f =>
      (f.empresa_nombre || '').toLowerCase().includes(q) ||
      (f.numero || '').toLowerCase().includes(q) ||
      String((f.importes || {}).total || '').includes(q) ||
      (f.cliente_datos?.cif || '').toLowerCase().includes(q)
    );
  }, [facturas, filtros.q]);

  const alertaClick = (tipo) => {
    if (tipo === 'tarjeta_fallidas') setFiltros({ ...filtros, estado: 'fallida', metodo: 'tarjeta' });
    else if (tipo === 'transferencias_atrasadas') setFiltros({ ...filtros, estado: 'pendiente', metodo: 'transferencia' });
    else if (tipo === 'sepa_rechazados') setFiltros({ ...filtros, estado: 'impago', metodo: 'sepa' });
  };

  const descargar = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al descargar');
    }
  };

  const exportarCSV = () => {
    const params = new URLSearchParams();
    if (filtros.empresa_id && filtros.empresa_id !== 'todos') params.set('empresa_id', filtros.empresa_id);
    if (filtros.estado && filtros.estado !== 'todos') params.set('estado', filtros.estado);
    if (filtros.metodo && filtros.metodo !== 'todos') params.set('metodo', filtros.metodo);
    if (filtros.fecha_desde) params.set('fecha_desde', filtros.fecha_desde);
    if (filtros.fecha_hasta) params.set('fecha_hasta', filtros.fecha_hasta);
    descargar(`/superadmin/facturas/export.csv?${params.toString()}`, `facturas_${new Date().toISOString().slice(0,10)}.csv`);
  };

  const abrirTimeline = async (empresa_id) => {
    try {
      const r = await api.get(`/superadmin/empresas/${empresa_id}/timeline`);
      setTimeline(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el historial');
    }
  };

  const totales = useMemo(() => {
    let pendiente = 0, pagado = 0, fallido = 0;
    for (const f of visibles) {
      const t = (f.importes || {}).total || 0;
      if (f.estado === 'pagada') pagado += t;
      else if (f.estado === 'fallida' || f.estado === 'impago') fallido += t;
      else if (f.estado === 'pendiente') pendiente += t;
    }
    return { pendiente, pagado, fallido, count: visibles.length };
  }, [visibles]);

  return (
    <div className="space-y-4" data-testid="facturas-global-tab">
      {/* KPIs */}
      <KPIs kpis={kpis} onAlertClick={alertaClick} />

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2 text-lg"><Filter className="h-5 w-5" /> Buscador de facturas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Búsqueda libre (empresa, nº, CIF)</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-400" />
                <Input className="pl-8" placeholder="Buscar..." value={filtros.q}
                  onChange={e => setFiltros({ ...filtros, q: e.target.value })}
                  data-testid="filtro-q" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Empresa</Label>
              <Select value={filtros.empresa_id} onValueChange={v => setFiltros({ ...filtros, empresa_id: v })}>
                <SelectTrigger data-testid="filtro-empresa"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las empresas</SelectItem>
                  {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={filtros.estado} onValueChange={v => setFiltros({ ...filtros, estado: v })}>
                <SelectTrigger data-testid="filtro-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTADOS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Método de pago</Label>
              <Select value={filtros.metodo} onValueChange={v => setFiltros({ ...filtros, metodo: v })}>
                <SelectTrigger data-testid="filtro-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METODOS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ciclo</Label>
              <Select value={filtros.ciclo} onValueChange={v => setFiltros({ ...filtros, ciclo: v })}>
                <SelectTrigger data-testid="filtro-ciclo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CICLOS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fecha desde</Label>
              <Input type="date" value={filtros.fecha_desde} onChange={e => setFiltros({ ...filtros, fecha_desde: e.target.value })} data-testid="filtro-fecha-desde" />
            </div>
            <div>
              <Label className="text-xs">Fecha hasta</Label>
              <Input type="date" value={filtros.fecha_hasta} onChange={e => setFiltros({ ...filtros, fecha_hasta: e.target.value })} data-testid="filtro-fecha-hasta" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Importe min</Label>
                <Input type="number" step="0.01" value={filtros.importe_min} onChange={e => setFiltros({ ...filtros, importe_min: e.target.value })} data-testid="filtro-min" />
              </div>
              <div>
                <Label className="text-xs">Importe max</Label>
                <Input type="number" step="0.01" value={filtros.importe_max} onChange={e => setFiltros({ ...filtros, importe_max: e.target.value })} data-testid="filtro-max" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-zinc-100">
            <div className="text-xs text-zinc-600 flex items-center gap-3">
              <b>{totales.count}</b> facturas ·
              Pagadas <b className="text-emerald-700">{fmtEUR(totales.pagado)}</b> ·
              Pendientes <b className="text-amber-700">{fmtEUR(totales.pendiente)}</b> ·
              Fallidas/Impago <b className="text-red-700">{fmtEUR(totales.fallido)}</b>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setFiltros({ q: '', empresa_id: 'todos', estado: 'todos', metodo: 'todos', ciclo: 'todos', fecha_desde: '', fecha_hasta: '', numero: '', importe_min: '', importe_max: '' })} data-testid="limpiar-filtros">
                Limpiar filtros
              </Button>
              <Button size="sm" onClick={exportarCSV} className="bg-zinc-900" data-testid="export-csv">
                <Download className="h-4 w-4 mr-1" /> Exportar CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2 text-lg"><Receipt className="h-5 w-5" /> Facturas</CardTitle>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-zinc-500 bg-zinc-50">
                <tr>
                  <th className="text-left py-2 px-3">Nº</th>
                  <th className="text-left py-2 px-3">Empresa</th>
                  <th className="text-left py-2 px-3">Fecha</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-left py-2 px-3">Método</th>
                  <th className="text-left py-2 px-3">Estado</th>
                  <th className="text-right py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {visibles.map(f => (
                  <FacturaRow key={f.id} f={f}
                    onOpenModal={setModal}
                    onOpenTimeline={() => abrirTimeline(f.empresa_id)}
                    onDescargarPdf={() => descargar(`/superadmin/facturas/${f.id}/pdf`, `factura_${f.numero}.pdf`)}
                    onReenviarEmail={async () => {
                      try {
                        const r = await api.post(`/superadmin/facturas/${f.id}/reenviar-email`);
                        toast.success(`Factura enviada a ${r.data.email}`);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Error al reenviar'); }
                    }}
                  />
                ))}
                {visibles.length === 0 && !loading && (
                  <tr><td colSpan={7} className="py-10 text-center text-zinc-400">No hay facturas que coincidan con los filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recordatorios (se mantiene) */}
      <RecordatoriosCard />

      {/* Modales */}
      <MarcarPagadaDialog
        open={modal?.tipo === 'pagada'} factura={modal?.factura}
        onClose={() => setModal(null)}
        onOk={async (data) => {
          try {
            await api.post(`/superadmin/facturas/${modal.factura.id}/marcar-pagada-v2`, data);
            toast.success('Factura marcada como pagada');
            setModal(null); cargarFacturas(); cargarKpis();
          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        }}
      />
      <MarcarImpagoDialog
        open={modal?.tipo === 'impago'} factura={modal?.factura}
        onClose={() => setModal(null)}
        onOk={async (motivo) => {
          try {
            await api.post(`/superadmin/facturas/${modal.factura.id}/marcar-impago`, { motivo });
            toast.success('Marcada como impago');
            setModal(null); cargarFacturas(); cargarKpis();
          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        }}
      />
      <AnularDialog
        open={modal?.tipo === 'anular'} factura={modal?.factura}
        onClose={() => setModal(null)}
        onOk={async (motivo) => {
          try {
            const r = await api.post(`/superadmin/facturas/${modal.factura.id}/anular`, { motivo });
            toast.success(`Anulada. Rectificativa ${r.data.rectificativa.numero} emitida.`);
            setModal(null); cargarFacturas(); cargarKpis();
          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        }}
      />
      <SepaXmlDialog
        open={modal?.tipo === 'sepa'} factura={modal?.factura}
        onClose={() => setModal(null)}
        onOk={async (fecha) => {
          descargar(`/superadmin/facturas/${modal.factura.id}/sepa-xml?fecha_cobro=${fecha}`,
            `sepa_${modal.factura.numero}_${fecha}.xml`);
          setModal(null);
        }}
      />
      <ReintentarDialog
        open={modal?.tipo === 'reintentar'} factura={modal?.factura}
        onClose={() => setModal(null)}
        onOk={async () => {
          try {
            const r = await api.post(`/superadmin/facturas/${modal.factura.id}/reintentar-cobro`);
            toast.success(`Reintento OK — Estado: ${r.data.estado}`);
            setModal(null); cargarFacturas(); cargarKpis();
          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        }}
      />
      <TimelineDialog data={timeline} onClose={() => setTimeline(null)} />
    </div>
  );
}

function KPIs({ kpis, onAlertClick }) {
  if (!kpis) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => <Card key={i}><CardContent className="p-4 h-20 animate-pulse bg-zinc-100" /></Card>)}
    </div>;
  }
  const alerta = kpis.alertas || {};
  const totalAlertas = (alerta.tarjeta_fallidas || 0) + (alerta.transferencias_atrasadas || 0) + (alerta.sepa_rechazados || 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={TrendingUp} color="emerald" label="MRR estimado" value={fmtEUR(kpis.mrr)} sub={`ARR ~ ${fmtEUR(kpis.arr)}`} testid="kpi-mrr" />
        <KPI icon={Wallet} color="blue" label="Cobrado este mes" value={fmtEUR(kpis.cobrado_mes)} sub={`${kpis.suscripciones_activas} suscripciones activas`} testid="kpi-cobrado" />
        <KPI icon={Clock} color="amber" label="Pendiente de cobro" value={fmtEUR(kpis.pendiente_cobro)} sub={`${kpis.empresas_impagadas} empresas con impago`} testid="kpi-pendiente" />
        <KPI icon={AlertTriangle} color={kpis.tasa_fallo_pct > 5 ? 'red' : 'zinc'} label="Tasa fallo pago" value={`${kpis.tasa_fallo_pct}%`} sub="sobre total emitido" testid="kpi-fallo" />
      </div>
      {totalAlertas > 0 && (
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="font-semibold text-red-800">Alertas activas:</span>
            {alerta.tarjeta_fallidas > 0 && (
              <button onClick={() => onAlertClick('tarjeta_fallidas')} className="text-xs px-2 py-1 rounded-full bg-red-100 hover:bg-red-200 text-red-800 border border-red-300" data-testid="alerta-tarjeta">
                🔴 {alerta.tarjeta_fallidas} tarjeta(s) fallida(s)
              </button>
            )}
            {alerta.transferencias_atrasadas > 0 && (
              <button onClick={() => onAlertClick('transferencias_atrasadas')} className="text-xs px-2 py-1 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300" data-testid="alerta-transf">
                🟡 {alerta.transferencias_atrasadas} transferencia(s) &gt;7 días
              </button>
            )}
            {alerta.sepa_rechazados > 0 && (
              <button onClick={() => onAlertClick('sepa_rechazados')} className="text-xs px-2 py-1 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300" data-testid="alerta-sepa">
                🟠 {alerta.sepa_rechazados} SEPA rechazado(s)
              </button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPI({ icon: Icon, color, label, value, sub, testid }) {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-700',
  }[color] || 'bg-zinc-50';
  return (
    <Card className={bg} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
          </div>
          <Icon className="h-5 w-5 opacity-60" />
        </div>
      </CardContent>
    </Card>
  );
}

function FacturaRow({ f, onOpenModal, onOpenTimeline, onDescargarPdf, onReenviarEmail }) {
  const total = (f.importes || {}).total || 0;
  const puedeMarcarPagada = ['pendiente', 'impago', 'fallida'].includes(f.estado);
  const puedeAnular = !['anulada', 'rectificativa'].includes(f.estado);
  const puedeReintentar = f.estado === 'fallida' && f.metodo_pago === 'tarjeta';
  const puedeSepaXml = f.metodo_pago === 'sepa' && ['pendiente', 'impago'].includes(f.estado);
  return (
    <tr className="hover:bg-zinc-50" data-testid={`factura-row-${f.numero}`}>
      <td className="py-2 px-3 font-mono text-xs">
        <div className="font-semibold">{f.numero}</div>
        {f.factura_referencia_numero && <div className="text-[10px] text-zinc-400">↳ ref {f.factura_referencia_numero}</div>}
        {f.rectificativa_numero && <div className="text-[10px] text-purple-600">→ rect {f.rectificativa_numero}</div>}
      </td>
      <td className="py-2 px-3">
        <div className="font-medium">{f.empresa_nombre}</div>
        <div className="text-xs text-zinc-500">{f.cliente_datos?.cif}</div>
      </td>
      <td className="py-2 px-3 text-zinc-600 text-xs">{(f.created_at || '').slice(0, 10)}</td>
      <td className="py-2 px-3 text-right font-semibold">{fmtEUR(total)}</td>
      <td className="py-2 px-3">
        <Badge variant="outline" className="text-xs">{metodoLabel(f.metodo_pago)}</Badge>
      </td>
      <td className="py-2 px-3">
        <Badge variant="outline" className={`text-xs ${estadoBadge(f.estado)}`}>{f.estado || '—'}</Badge>
      </td>
      <td className="py-2 px-3">
        <div className="flex items-center justify-end gap-1">
          {puedeMarcarPagada && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onOpenModal({ tipo: 'pagada', factura: f })}
              data-testid={`marcar-pagada-${f.numero}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Cobrada
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`menu-${f.numero}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={onDescargarPdf}><FileText className="h-4 w-4 mr-2" /> Descargar PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={onReenviarEmail}><Mail className="h-4 w-4 mr-2" /> Reenviar por email</DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenTimeline}><History className="h-4 w-4 mr-2" /> Historial empresa</DropdownMenuItem>
              <DropdownMenuSeparator />
              {puedeSepaXml && (
                <DropdownMenuItem onClick={() => onOpenModal({ tipo: 'sepa', factura: f })}>
                  <FileDown className="h-4 w-4 mr-2" /> Generar XML SEPA
                </DropdownMenuItem>
              )}
              {puedeReintentar && (
                <DropdownMenuItem onClick={() => onOpenModal({ tipo: 'reintentar', factura: f })}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Reintentar cobro Stripe
                </DropdownMenuItem>
              )}
              {puedeMarcarPagada && (
                <DropdownMenuItem onClick={() => onOpenModal({ tipo: 'impago', factura: f })}>
                  <XCircle className="h-4 w-4 mr-2 text-orange-600" /> Marcar como impago
                </DropdownMenuItem>
              )}
              {puedeAnular && (
                <DropdownMenuItem className="text-red-700 focus:text-red-800 focus:bg-red-50" onClick={() => onOpenModal({ tipo: 'anular', factura: f })}>
                  <Ban className="h-4 w-4 mr-2" /> Anular (rectificativa)
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

function MarcarPagadaDialog({ open, factura, onClose, onOk }) {
  const [nota, setNota] = useState('');
  const [ref, setRef] = useState('');
  useEffect(() => { if (open) { setNota(''); setRef(''); } }, [open]);
  if (!factura) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Marcar factura {factura.numero} como pagada</DialogTitle>
          <DialogDescription>
            Importe: <b>{fmtEUR((factura.importes||{}).total)}</b> · Método: <b>{metodoLabel(factura.metodo_pago)}</b> · Empresa: <b>{factura.empresa_nombre}</b>.
            Se activará la suscripción y se dará por cobrada. Idempotente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Referencia bancaria (opcional)</Label>
            <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="Ej: TRF20260724-001" data-testid="ref-bancaria" />
          </div>
          <div>
            <Label>Nota interna (opcional)</Label>
            <Input value={nota} onChange={e => setNota(e.target.value)} placeholder="Comentario para el histórico" data-testid="nota-pago" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onOk({ nota, referencia_bancaria: ref })} data-testid="confirmar-pagada">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Confirmar cobro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarcarImpagoDialog({ open, factura, onClose, onOk }) {
  const [motivo, setMotivo] = useState('');
  useEffect(() => { if (open) setMotivo(''); }, [open]);
  if (!factura) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Marcar {factura.numero} como impago</DialogTitle>
          <DialogDescription>La deuda queda viva. No se emite rectificativa.</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Motivo</Label>
          <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Devuelto por el banco, saldo insuficiente..." data-testid="motivo-impago" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => onOk(motivo)} data-testid="confirmar-impago">Marcar impago</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnularDialog({ open, factura, onClose, onOk }) {
  const [motivo, setMotivo] = useState('');
  useEffect(() => { if (open) setMotivo(''); }, [open]);
  if (!factura) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-700">Anular factura {factura.numero}</DialogTitle>
          <DialogDescription>
            Se emitirá una <b>factura rectificativa</b> con importe negativo (obligatorio por ley — art. 15 RD 1619/2012 IVA).
            La factura original quedará marcada como <b>anulada</b> con referencia a la rectificativa.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Motivo de la anulación *</Label>
          <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: error en el importe, cancelación del cliente..." data-testid="motivo-anular" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-red-600 hover:bg-red-700" disabled={!motivo.trim()} onClick={() => onOk(motivo)} data-testid="confirmar-anular">
            <Ban className="h-4 w-4 mr-1" /> Anular y emitir rectificativa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SepaXmlDialog({ open, factura, onClose, onOk }) {
  const [fecha, setFecha] = useState('');
  useEffect(() => {
    if (open) {
      const d = new Date(); d.setDate(d.getDate() + 5);
      setFecha(d.toISOString().slice(0, 10));
    }
  }, [open]);
  if (!factura) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generar XML SEPA — {factura.numero}</DialogTitle>
          <DialogDescription>Se descargará el fichero pain.008 para subir al banco.</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Fecha de cobro</Label>
          <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} data-testid="sepa-fecha-cobro" />
          <p className="text-xs text-zinc-500 mt-1">Recomendado: +5 días laborables (siguiente ciclo bancario).</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-zinc-900" onClick={() => onOk(fecha)} disabled={!fecha} data-testid="generar-sepa-uno">
            <Download className="h-4 w-4 mr-1" /> Descargar XML
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReintentarDialog({ open, factura, onClose, onOk }) {
  if (!factura) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reintentar cobro Stripe — {factura.numero}</DialogTitle>
          <DialogDescription>
            Se pedirá a Stripe que vuelva a cobrar esta factura ({fmtEUR((factura.importes||{}).total)}). Si el cliente ha actualizado su tarjeta o hay saldo, el cobro se completará.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={onOk} data-testid="confirmar-reintentar">
            <RefreshCw className="h-4 w-4 mr-1" /> Reintentar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimelineDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historial de {data.empresa.nombre}</DialogTitle>
          <DialogDescription>Estado: <Badge variant="outline">{data.empresa.estado_cuenta}</Badge></DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {(data.eventos || []).map((e, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <span className="w-28 shrink-0 text-xs text-zinc-500 pt-0.5">{(e.at || '').replace('T', ' ').slice(0, 16)}</span>
              <div className="flex-1">
                <span className="font-medium">{e.descripcion}</span>
                {e.estado && <Badge variant="outline" className={`ml-2 text-[10px] ${estadoBadge(e.estado)}`}>{e.estado}</Badge>}
              </div>
            </div>
          ))}
          {(data.eventos || []).length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">Sin eventos.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordatoriosCard() {
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post('/superadmin/facturacion/recordatorios/ejecutar');
      toast.success(`${data.enviados} recordatorio(s) enviado(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setEnviando(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Recordatorios de pago</CardTitle>
        <CardDescription>Envía por email un aviso a las empresas cuyo cobro sea en ≤3 días y a las que la prueba caduque en ≤3 días. Se ejecuta también automáticamente cada 24 h.</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={enviar} disabled={enviando} className="bg-zinc-900" data-testid="enviar-recordatorios">
          {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Enviar recordatorios ahora
        </Button>
      </CardContent>
    </Card>
  );
}
